import type { QueryKey } from "@tanstack/react-query";
import { assetInstanceKeys, assetKeys } from "@/services/asset/asset.options";
import { jobKeys } from "@/services/job/job.options";
import { lifecycleKeys } from "@/services/lifecycle/lifecycle.options";
import {
	recordInstanceKeys,
	recordKeys,
} from "@/services/record/record.options";
import { ticketKeys } from "@/services/ticket/ticket.options";
import type { LiveEntityType } from "./local-entity-write-registry";

export interface LiveEntityQueryKeys {
	details: QueryKey[];
	lists: QueryKey[];
	counts: QueryKey[];
	history: QueryKey[];
	validStatuses: QueryKey[];
	isInstance: boolean;
}

export function mapEntityTypeToQueryKeys(
	entityType: string,
	entityId: string,
): LiveEntityQueryKeys | null {
	switch (entityType as LiveEntityType) {
		case "Job":
			return {
				details: [[...jobKeys.jobs(), "detail", entityId]],
				lists: [[...jobKeys.jobs(), "list"]],
				counts: [[...jobKeys.jobs(), "count"]],
				history: [jobKeys.jobHistory(entityId)],
				validStatuses: [jobKeys.jobsValidStatuses(entityId)],
				isInstance: true,
			};
		case "Ticket":
			return {
				details: [[...ticketKeys.tickets(), "detail", entityId]],
				lists: [[...ticketKeys.tickets(), "list"]],
				counts: [[...ticketKeys.tickets(), "count"]],
				history: [ticketKeys.ticketHistory(entityId)],
				validStatuses: [
					[...ticketKeys.tickets(), entityId, "valid-statuses"],
				],
				isInstance: true,
			};
		case "Asset":
			return {
				details: [
					[...assetInstanceKeys.details(), entityId],
					[...assetKeys.assets(), "detail", entityId],
				],
				lists: [assetInstanceKeys.lists()],
				counts: [
					assetInstanceKeys.counts(),
					[...assetKeys.types(), "assets-count"],
				],
				history: [assetInstanceKeys.history(entityId)],
				validStatuses: [assetInstanceKeys.validStatuses(entityId)],
				isInstance: true,
			};
		case "Record":
			return {
				details: [[...recordInstanceKeys.details(), entityId]],
				lists: [recordInstanceKeys.lists()],
				counts: [
					recordInstanceKeys.counts(),
					[...recordKeys.all, "records-count"],
				],
				history: [recordInstanceKeys.history(entityId)],
				validStatuses: [],
				isInstance: true,
			};
		case "JobType":
			return {
				details: [[...jobKeys.jobs(), "job-type-detail", entityId]],
				lists: [[...jobKeys.jobs(), "job-types"]],
				counts: [[...jobKeys.jobs(), "job-types", "count"]],
				history: [jobKeys.jobTypeHistory(entityId)],
				validStatuses: [],
				isInstance: false,
			};
		case "TicketType":
			return {
				details: [[...ticketKeys.tickets(), "ticket-type-detail", entityId]],
				lists: [[...ticketKeys.tickets(), "ticket-types"]],
				counts: [[...ticketKeys.tickets(), "ticket-types", "count"]],
				history: [ticketKeys.ticketTypeHistory(entityId)],
				validStatuses: [],
				isInstance: false,
			};
		case "AssetType":
			return {
				details: [[...assetKeys.types(), "detail", entityId]],
				lists: [
					[...assetKeys.types(), "all"],
					[...assetKeys.types(), "page"],
				],
				counts: [[...assetKeys.types(), "count"]],
				history: [assetKeys.assetTypeHistory(entityId)],
				validStatuses: [],
				isInstance: false,
			};
		case "RecordType":
			return {
				details: [[...recordKeys.types(), "detail", entityId]],
				lists: [
					[...recordKeys.types(), "all"],
					[...recordKeys.types(), "page"],
				],
				counts: [[...recordKeys.types(), "count"]],
				history: [recordKeys.recordTypeHistory(entityId)],
				validStatuses: [],
				isInstance: false,
			};
		case "StatusLifecycle":
			return {
				details: [[...lifecycleKeys.all, "detail", entityId]],
				lists: [
					lifecycleKeys.lists(),
					[...lifecycleKeys.all, "status-lifecycles"],
					[...lifecycleKeys.all, "page"],
				],
				counts: [[...lifecycleKeys.all, "count"]],
				history: [[...lifecycleKeys.all, "history", entityId]],
				validStatuses: [],
				isInstance: false,
			};
		default:
			return null;
	}
}
